/**
 * DSL_SmallWindow
 * HUD-style small window component (33% smaller than DSL_Window)
 */

import { useMemo, memo, useCallback } from 'react'
import { getDeviceInfo } from '../../utils/deviceDetection'

const HUD_COLOR = '#ee3a17'

const DSL_SmallWindow = memo(function DSL_SmallWindow({ 
  onClose, 
  isClosing, 
  isTransitioning, 
  scrollContentRef, 
  children,
  offsetTop,
  offsetRight,
  offsetBottom,
  offsetLeft,
  hideCloseButton = false,
  allowButtonClicks = false,
  noBackdrop = false,
  containerClassName = '',
  chamferScale = 1,
  side = 'left'   // 'left' | 'right' | 'center'
}) {
  // Enable flicker effects for all devices (memory leaks fixed)
  const shouldFlicker = isTransitioning
  
  // Handle click on backdrop to close (only if close button is enabled)
  const handleContainerClick = useCallback((e) => {
    if (!hideCloseButton && e.target.classList.contains('dsl-small-window-container')) {
      onClose()
    }
  }, [onClose, hideCloseButton])
  
  // Build position style object based on provided offsets
  const positionStyle = useMemo(() => {
    const style = {}
    if (offsetTop !== undefined) style.top = offsetTop
    if (offsetRight !== undefined) style.right = offsetRight
    if (offsetBottom !== undefined) style.bottom = offsetBottom
    if (offsetLeft !== undefined) style.left = offsetLeft
    return style
  }, [offsetTop, offsetRight, offsetBottom, offsetLeft])
  
  return (
    <div 
      className={`dsl-small-window-container${noBackdrop ? ' no-backdrop' : ''}${containerClassName ? ` ${containerClassName}` : ''}${side !== 'left' ? ` panel-${side}` : ''}`}
      style={{ pointerEvents: allowButtonClicks ? 'none' : 'auto' }}
      onClick={handleContainerClick}
    >
      <div 
        className={`dsl-small-window${hideCloseButton ? ' no-close' : ''} ${isClosing ? 'closing' : ''} ${shouldFlicker ? 'flickering' : ''}`}
        style={{
          ...positionStyle,
          pointerEvents: 'auto',
          '--sw-a': `${Math.round(46 * chamferScale)}px`,
          '--sw-b': `${Math.round(20 * chamferScale)}px`,
          '--sw-c': `${Math.round(48 * chamferScale)}px`,
        }}
      >
        <div className="dsl-small-window-background" />
        <DSL_SmallWindowFrame scale={chamferScale} hasCloseButton={!hideCloseButton} />
        
        {!hideCloseButton && (
          <button className="dsl-small-window-close" onClick={onClose}>
            ×
          </button>
        )}
        
        <div ref={scrollContentRef} className="dsl-small-window-content">
          {children}
        </div>
      </div>
    </div>
  )
})

function DSL_SmallWindowFrame({ scale = 1, hasCloseButton = true }) {
  // Use full-size dimensions - CSS will handle scaling per breakpoint
  const c1 = Math.round(80 * scale)
  const c2 = Math.round(40 * scale)
  const c3 = Math.round(96 * scale)
  const c4 = Math.round(48 * scale)
  const strokeWidth = 1.5
  const strokeWidthThin = 1.5
  
  return (
    <>
      {/* Top left corner */}
      <svg 
        className="dsl-small-window-corner dsl-small-window-corner-tl"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: c1,
          height: c1,
          overflow: 'visible'
        }}
        viewBox={`0 0 ${c1} ${c1}`}
      >
        <line x1="0" y1={c1} x2="0" y2={c2} stroke={HUD_COLOR} strokeWidth={strokeWidth} />
        <line x1="0" y1={c2} x2={c2} y2="0" stroke={HUD_COLOR} strokeWidth={strokeWidthThin} />
        <line x1={c2} y1="0" x2={c1} y2="0" stroke={HUD_COLOR} strokeWidth={strokeWidth} />
      </svg>
      
      {/* Top right corner — open pocket when X button present, closed chamfer when not */}
      {hasCloseButton ? (
        <svg 
          className="dsl-small-window-corner dsl-small-window-corner-tr"
          style={{ position: 'absolute', top: 0, right: 0, width: c3, height: c3, overflow: 'visible' }}
          viewBox={`0 0 ${c3} ${c3}`}
        >
          <line x1="0" y1="0" x2={c3 - c4} y2="0" stroke={HUD_COLOR} strokeWidth={strokeWidth} />
          <line x1={c3} y1={c4} x2={c3} y2={c3} stroke={HUD_COLOR} strokeWidth={strokeWidth} />
        </svg>
      ) : (
        <svg 
          className="dsl-small-window-corner dsl-small-window-corner-tr"
          style={{ position: 'absolute', top: 0, right: 0, width: c1, height: c1, overflow: 'visible' }}
          viewBox={`0 0 ${c1} ${c1}`}
        >
          <line x1={c1} y1={c1} x2={c1} y2={c2} stroke={HUD_COLOR} strokeWidth={strokeWidth} />
          <line x1={c1} y1={c2} x2={c1 - c2} y2="0" stroke={HUD_COLOR} strokeWidth={strokeWidthThin} />
          <line x1={c1 - c2} y1="0" x2="0" y2="0" stroke={HUD_COLOR} strokeWidth={strokeWidth} />
        </svg>
      )}
      
      {/* Bottom left corner */}
      <svg 
        className="dsl-small-window-corner dsl-small-window-corner-bl"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: c1,
          height: c1,
          overflow: 'visible'
        }}
        viewBox={`0 0 ${c1} ${c1}`}
      >
        <line x1="0" y1="0" x2="0" y2={c1 - c2} stroke={HUD_COLOR} strokeWidth={strokeWidth} />
        <line x1="0" y1={c1 - c2} x2={c2} y2={c1} stroke={HUD_COLOR} strokeWidth={strokeWidthThin} />
        <line x1={c2} y1={c1} x2={c1} y2={c1} stroke={HUD_COLOR} strokeWidth={strokeWidth} />
      </svg>
      
      {/* Bottom right corner */}
      <svg 
        className="dsl-small-window-corner dsl-small-window-corner-br"
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: c1,
          height: c1,
          overflow: 'visible'
        }}
        viewBox={`0 0 ${c1} ${c1}`}
      >
        <line x1="0" y1={c1} x2={c1 - c2} y2={c1} stroke={HUD_COLOR} strokeWidth={strokeWidth} />
        <line x1={c1 - c2} y1={c1} x2={c1} y2={c1 - c2} stroke={HUD_COLOR} strokeWidth={strokeWidthThin} />
        <line x1={c1} y1={c1 - c2} x2={c1} y2="0" stroke={HUD_COLOR} strokeWidth={strokeWidth} />
      </svg>
    </>
  )
}

export default DSL_SmallWindow